import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { useEffect, useState, useMemo } from 'react';
import { WidgetActions } from '../common/WidgetActions';
import { useWidgetPopout } from '~/hooks/useWidgetPopout';

interface EMSBlotterWidgetProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
}

interface Order {
  id: string;
  type: string;
  side: 'BUY' | 'SELL';
  security: string;
  identifier: string;
  quantity: number;
  status: 'NEW' | 'FILLED' | 'PARTIAL';
  sb: boolean;
  gd: boolean;
  action: string;
  leave: boolean;
  ib: boolean;
  rfm?: string;
  executionInstructions?: string;
  price: number;
  isNew?: boolean;
}

interface Execution {
  id: string;
  executionTime: string;
  timeCreated: string;
  type: string;
  side: 'BUY' | 'SELL';
  security: string;
  quantity: number;
  status: string;
  gd: boolean;
  price: number;
  executionPrice: number;
}

export const EMSBlotterWidget = ({
  widgetId,
  handleRemoveWidget,
}: EMSBlotterWidgetProps) => {
  const [filter, setFilter] = useState<string>('ALL');
  const [showExecutions, setShowExecutions] = useState<boolean>(true);
  const [orders, setOrders] = useState<Order[]>([]);

  // Add popout functionality
  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: EMSBlotterWidget,
    title: 'EMS Blotter',
    defaultWidth: 1200,
    defaultHeight: 800,
    widgetProps: {
      widgetId,
      handleRemoveWidget,
    },
  });

  const mockData = useMemo<Order[]>(
    () => [
      {
        id: '1',
        type: 'I',
        side: 'BUY',
        security: '>VOD 1 09/11/20 BTIN',
        identifier: 'COEK4807484',
        quantity: 100,
        status: 'NEW',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        price: 102.901,
      },
      {
        id: '2',
        type: 'I',
        side: 'SELL',
        security: '>WZ 4 ½ 09/15/20',
        identifier: 'COEJ8170411',
        quantity: 250,
        status: 'NEW',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        price: 107.38,
      },
      {
        id: '3',
        type: 'I',
        side: 'BUY',
        security: '>DIS 4 ⅛ 08/16/41',
        identifier: 'COEI7873090',
        quantity: 250,
        status: 'NEW',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        price: 110.988,
      },
      {
        id: '4',
        type: 'I',
        side: 'BUY',
        security: 'MID OR BETTER',
        identifier: '',
        quantity: 0,
        status: 'NEW',
        sb: true,
        gd: false,
        action: 'Send',
        leave: true,
        ib: false,
        price: 0,
      },
      {
        id: '5',
        type: 'I',
        side: 'SELL',
        security: '>MSFT 2 ¾ 02/12/25',
        identifier: 'COEK5982341',
        quantity: 175,
        status: 'NEW',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        price: 98.75,
      },
      {
        id: '6',
        type: 'I',
        side: 'BUY',
        security: '>AAPL 1 ⅞ 04/30/23',
        identifier: 'COEJ6283947',
        quantity: 300,
        status: 'NEW',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        price: 99.625,
      },
      {
        id: '7',
        type: 'I',
        side: 'SELL',
        security: '>AMZN 3 ⅛ 07/15/28',
        identifier: 'COEI8463029',
        quantity: 125,
        status: 'NEW',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        price: 103.25,
      },
      {
        id: '8',
        type: 'I',
        side: 'BUY',
        security: '>GOOGL 2 ¼ 11/05/26',
        identifier: 'COEK2365894',
        quantity: 200,
        status: 'NEW',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        price: 101.375,
      },
      {
        id: '9',
        type: 'I',
        side: 'SELL',
        security: '>NFLX 4 ⅜ 06/30/30',
        identifier: 'COEJ7493021',
        quantity: 150,
        status: 'NEW',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        price: 112.625,
      },
      {
        id: '10',
        type: 'I',
        side: 'BUY',
        security: '>META 3 ⅝ 03/15/27',
        identifier: 'COEI5729483',
        quantity: 225,
        status: 'PARTIAL',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        rfm: 'ICEBERG',
        executionInstructions: 'PARTICIPATE 50%',
        price: 105.875,
      },
      {
        id: '11',
        type: 'I',
        side: 'SELL',
        security: '>TSLA 5 ¼ 08/01/25',
        identifier: 'COEK9871236',
        quantity: 100,
        status: 'FILLED',
        sb: true,
        gd: true,
        action: 'Send',
        leave: false,
        ib: true,
        rfm: 'TWAP',
        executionInstructions: '9:30-16:00',
        price: 118.25,
      },
      {
        id: '12',
        type: 'I',
        side: 'BUY',
        security: '>NVDA 2 ⅞ 12/10/24',
        identifier: 'COEJ3648271',
        quantity: 275,
        status: 'PARTIAL',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        rfm: 'VWAP',
        executionInstructions: '10:00-15:30',
        price: 104.125,
      },
    ],
    [],
  );

  const executionsData: Execution[] = [
    {
      id: '1',
      executionTime: '09:35:22',
      timeCreated: '09:30:15',
      type: 'I',
      side: 'SELL',
      security: '>TSLA 5 ¼ 08/01/25',
      quantity: 100,
      status: 'FILLED',
      gd: true,
      price: 118.25,
      executionPrice: 118.275,
    },
    {
      id: '2',
      executionTime: '10:12:45',
      timeCreated: '10:05:30',
      type: 'I',
      side: 'BUY',
      security: '>META 3 ⅝ 03/15/27',
      quantity: 125,
      status: 'PARTIAL',
      gd: true,
      price: 105.875,
      executionPrice: 105.9,
    },
    {
      id: '3',
      executionTime: '10:45:18',
      timeCreated: '10:30:22',
      type: 'I',
      side: 'BUY',
      security: '>NVDA 2 ⅞ 12/10/24',
      quantity: 150,
      status: 'PARTIAL',
      gd: true,
      price: 104.125,
      executionPrice: 104.15,
    },
  ];

  const sideTemplate = (rowData: Order | Execution) => {
    return (
      <span
        className={`text-${rowData.side === 'BUY' ? 'green-500' : 'red-500'} font-bold`}
      >
        {rowData.side}
      </span>
    );
  };

  const statusTemplate = (rowData: Order) => {
    let severity: 'info' | 'success' | 'warning' | 'danger' | null = null;
    switch (rowData.status) {
      case 'NEW':
        severity = 'info';
        break;
      case 'FILLED':
        severity = 'success';
        break;
      case 'PARTIAL':
        severity = 'warning';
        break;
      default:
        severity = 'info';
    }
    return <Tag value={rowData.status} severity={severity} />;
  };

  const checkboxTemplate = (rowData: Order, field: keyof Order) => {
    return rowData[field] ? (
      <i
        className="pi pi-check"
        style={{ color: 'var(--bq-color-positive)' }}
      />
    ) : null;
  };

  const actionTemplate = (rowData: Order) => {
    return (
      <Button label={rowData.action} severity="info" text className="p-0" />
    );
  };

  const leaveTemplate = (rowData: Order) => {
    return rowData.leave ? (
      <Button
        label="Leave"
        severity="info"
        className="p-1 bg-blue-100 text-blue-700 border-none"
      />
    ) : null;
  };

  const filterOptions = [
    { label: 'All', value: 'ALL' },
    { label: 'Filled/Confirmed', value: 'FILLED/CONFIRMED' },
    { label: 'New', value: 'NEW' },
    { label: 'Partial', value: 'PARTIAL' },
  ];

  const header = (
    <div className="flex flex-row justify-content-between align-items-center p-2">
      <div className="flex align-items-center">
        <span className="font-bold mr-1">OM Blotter</span>
        <Dropdown
          value={filter}
          options={filterOptions}
          onChange={(e) => setFilter(e.value)}
          placeholder="Filter"
          className="w-10rem"
          panelClassName="compact-dropdown"
          style={{ fontSize: '0.85rem' }}
        />
      </div>
      <div className="flex align-items-center">
        <Button
          className="ems-blotter-toggle-executions hover-icon"
          icon={showExecutions ? 'pi pi-eye-slash' : 'pi pi-eye'}
          onClick={() => setShowExecutions(!showExecutions)}
          title={showExecutions ? 'Hide Executions' : 'Show Executions'}
          data-pr-tooltip={
            showExecutions ? 'Hide Executions' : 'Show Executions'
          }
          text
        />
        {handleRemoveWidget && widgetId && (
          <WidgetActions
            onRemove={() => handleRemoveWidget(widgetId)}
            onPopout={handlePopout}
            widgetId={widgetId}
          />
        )}
      </div>
    </div>
  );

  const executionsHeader = (
    <div
      className="flex flex-row justify-content-between align-items-center p-1 border-top-1 mt-1"
      style={{ borderColor: 'var(--bq-border-muted)' }}
    >
      <div className="flex align-items-center">
        <span className="font-bold mr-1">Executions</span>
        <span className="text-sm" style={{ color: 'var(--bq-text-tertiary)' }}>
          Filter: FILLED/CONFIRMED
        </span>
      </div>
    </div>
  );

  useEffect(() => {
    // Initialize orders with mock data
    setOrders(mockData);

    // Set up timer to add new AAPL order
    const timer = setTimeout(() => {
      const newOrder: Order = {
        id: String(Date.now()),
        type: 'I',
        side: 'SELL',
        security: '>AAPL 1 ⅞ 04/30/23',
        identifier: 'COEJ' + Math.floor(Math.random() * 10000000),
        quantity: 100000,
        status: 'NEW',
        sb: true,
        gd: true,
        action: 'Send',
        leave: true,
        ib: true,
        price: 99.625,
        isNew: true,
      };

      setOrders((prevOrders) => [newOrder, ...prevOrders]);

      // Remove the flash after animation completes
      setTimeout(() => {
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.id === newOrder.id ? { ...order, isNew: false } : order,
          ),
        );
      }, 1600); // Slightly longer than animation duration
    }, 10000);

    return () => clearTimeout(timer);
  }, [mockData, setOrders]);

  // Row class function to apply flash-row class for new orders
  const rowClassName = (data: Order) => {
    return data.isNew ? 'flash-row' : '';
  };

  return (
    <Card
      className="w-full h-full ems-blotter-widget p-card hover-container"
      header={header}
    >
      <div className="w-full h-full p-0">
        <div className="flex h-full flex-column">
          <div className={showExecutions ? 'h-6' : 'flex-1'}>
            <DataTable
              value={orders}
              scrollable
              scrollHeight="100%"
              resizableColumns
              showGridlines
              stripedRows
              size="small"
              className="ems-blotter-table"
              style={{ margin: 0 }}
              rowClassName={rowClassName}
            >
              <Column field="type" header="Type" style={{ width: '50px' }} />
              <Column
                field="side"
                header="Side"
                body={sideTemplate}
                style={{ width: '80px' }}
              />
              <Column
                field="security"
                header="Security"
                style={{ width: '200px' }}
              />
              <Column
                field="identifier"
                header="Unique Bloomberg"
                style={{ width: '150px' }}
              />
              <Column
                field="quantity"
                header="Qty (M)"
                style={{ width: '100px' }}
              />
              <Column
                field="status"
                header="Status"
                body={statusTemplate}
                style={{ width: '100px' }}
              />
              <Column
                field="sb"
                header="SB"
                body={(rowData) => checkboxTemplate(rowData, 'sb')}
                style={{ width: '60px' }}
              />
              <Column
                field="gd"
                header="GD"
                body={(rowData) => checkboxTemplate(rowData, 'gd')}
                style={{ width: '60px' }}
              />
              <Column
                field="action"
                header="Action"
                body={actionTemplate}
                style={{ width: '80px' }}
              />
              <Column
                field="leave"
                header="Leave"
                body={leaveTemplate}
                style={{ width: '80px' }}
              />
              <Column
                field="ib"
                header="IB"
                body={(rowData) => checkboxTemplate(rowData, 'ib')}
                style={{ width: '60px' }}
              />
              <Column field="rfm" header="RFM" style={{ width: '100px' }} />
              <Column
                field="executionInstructions"
                header="Execution Instructions"
                style={{ width: '200px' }}
              />
              <Column
                field="price"
                header="BGN Price"
                style={{ width: '100px' }}
              />
            </DataTable>
          </div>

          {showExecutions && (
            <>
              {executionsHeader}
              <div className="flex-1">
                <DataTable
                  value={executionsData}
                  scrollable
                  scrollHeight="100%"
                  resizableColumns
                  showGridlines
                  stripedRows
                  size="small"
                  className="ems-blotter-table mt-1"
                  style={{ margin: 0 }}
                >
                  <Column
                    field="executionTime"
                    header="Execution Time"
                    style={{ width: '120px' }}
                  />
                  <Column
                    field="timeCreated"
                    header="Time Created"
                    style={{ width: '120px' }}
                  />
                  <Column
                    field="type"
                    header="Type"
                    style={{ width: '50px' }}
                  />
                  <Column
                    field="side"
                    header="Side"
                    body={sideTemplate}
                    style={{ width: '80px' }}
                  />
                  <Column
                    field="security"
                    header="Security"
                    style={{ width: '200px' }}
                  />
                  <Column
                    field="quantity"
                    header="Qty (M)"
                    style={{ width: '100px' }}
                  />
                  <Column
                    field="status"
                    header="Status"
                    style={{ width: '100px' }}
                  />
                  <Column field="gd" header="GD" style={{ width: '60px' }} />
                  <Column
                    field="price"
                    header="BGN Price"
                    style={{ width: '100px' }}
                  />
                  <Column
                    field="executionPrice"
                    header="Exec. Price"
                    style={{ width: '100px' }}
                  />
                </DataTable>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default EMSBlotterWidget;
